import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import M from 'materialize-css';
import { FastspringSubscriptionName } from '../../../common/enums.ts';
import mutationHooks from '../../../common/mutationHooks/index.ts';
import queryHooks from '../../../common/queryHooks/index.ts';
import useCurrentCustomerLocationId from '../../../hooks/useCurrentCustomerLocation.ts';

interface IProps {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  userType: 'customer' | 'employee' | 'trading partner' | undefined;
}

const validSubscriptions = Object.freeze([
  FastspringSubscriptionName.medtraxBasicService,
  FastspringSubscriptionName.medtraxBasicServiceAnnual,
  FastspringSubscriptionName.medtraxEnterpriseService,
  FastspringSubscriptionName.medtraxEnterpriseServiceAnnual,
  FastspringSubscriptionName.medtraxEssentialsService,
  FastspringSubscriptionName.medtraxEssentialsServiceAnnual,
  FastspringSubscriptionName.medtraxMMCAPService,
  FastspringSubscriptionName.medtraxPlusService,
  FastspringSubscriptionName.medtraxPlusServiceAnnual,
  FastspringSubscriptionName.medtraxPremierService,
]) as string[];

const DeactivateLocationModal = ({ setIsOpen, isOpen, userType }: IProps) => {
  const { customerLocationId } = useCurrentCustomerLocationId();
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);

  const { data: subscriptions, isSuccess: subsReady } =
    queryHooks.fastspring.useReadAllSubscriptionsById();
  const { data: isAdmin } = queryHooks.user.useGetIsAdminById(
    { customerLocationId: Number(customerLocationId) },
    {
      refetchOnMount: true,
    }
  );
  const { data: customerLocations, isSuccess: locationsReady } =
    queryHooks.customerLocation.useReadAllHydratedByUserId();
  const { mutate: deactivateCustomerLocations } =
    mutationHooks.customerLocations.useDeactivateLocations();

  const totalSubscriptions = useMemo(() => {
    if (!subscriptions) return 0;
    return subscriptions
      ?.filter(sub => sub.active && validSubscriptions.includes(sub.product))
      .reduce((acc, sub) => acc + sub.quantity, 0);
  }, [subscriptions]);

  const activeLocations = useMemo(() => {
    return customerLocations?.filter(location => !!location.isActive) ?? [];
  }, [customerLocations]);

  const numUnusedLocations = totalSubscriptions - activeLocations.length;
  const requiredDeactivations = Math.max(0, -numUnusedLocations);
  const ready = subsReady && locationsReady;

  useLayoutEffect(() => {
    if (!ready) return;
    setIsOpen(numUnusedLocations < 0);
  }, [ready, setIsOpen, numUnusedLocations, subscriptions, customerLocations]);

  const modalInstanceRef = useRef<M.Modal | null>(null);
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;

    modalInstanceRef.current = M.Modal.init(el, {
      dismissible: userType === 'employee',
      onCloseEnd: () => setIsOpen(false),
    });

    if (isOpen) {
      modalInstanceRef.current.open();
    }

    return () => {
      modalInstanceRef.current?.destroy();
      modalInstanceRef.current = null;
    };
  }, [setIsOpen, isOpen]);

  useEffect(() => {
    const instance = modalInstanceRef.current;
    if (!instance) return;

    if (isOpen) instance.open();
    else instance.close();
  }, [isOpen]);

  const onCheckboxOnClick = (locationId: number) => {
    if (!selectedLocationIds.includes(locationId)) {
      if (requiredDeactivations <= selectedLocationIds.length) return;
      setSelectedLocationIds([...selectedLocationIds, locationId]);
    } else {
      setSelectedLocationIds(
        selectedLocationIds.filter(id => id !== locationId)
      );
    }
  };

  const handleDeactivate = (e: React.MouseEvent<HTMLButtonElement>): void => {
    deactivateCustomerLocations(selectedLocationIds);
    setSelectedLocationIds([]);
  };

  if (!ready) return null;
  return (
    <div ref={modalRef} className="modal">
      <div className="modal-content">
        <div>
          <h3 className="modal-title" id="modal-title">
            Your active locations exceed your subscriptions
          </h3>
          <h5 className="modal-title" id="modal-description">
            Please choose {requiredDeactivations} location/s to deactivate.{' '}
            <br />
            This action must be completed before you can manage your locations.
          </h5>
          {activeLocations?.map(location => (
            <div key={location.id}>
              <label>
                <input
                  type="checkbox"
                  className="filled-in"
                  checked={selectedLocationIds.includes(location.id)}
                  onChange={() => onCheckboxOnClick(location.id)}
                />
                <span>{location.name}</span>
              </label>
            </div>
          ))}
          <div className={'modal-footer'}>
            <button
              className="btn"
              onClick={handleDeactivate}
              disabled={!isAdmin}
            >
              Deactivate
            </button>
            {!isAdmin && (
              <span style={{ color: 'red', paddingLeft: '10px' }}>
                You must be an Admin to deactivate locations
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeactivateLocationModal;
